import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Upload, X, FileText, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

const TYPES = [
  { id: "print_file", label: "Tenho um ficheiro pronto para imprimir" },
  { id: "modify_file", label: "Preciso de alterar um ficheiro" },
  { id: "new_model", label: "Preciso de criar um modelo 3D" },
  { id: "reverse_eng", label: "Tenho uma peça física para reproduzir" },
  { id: "idea", label: "Tenho uma ideia — precisa de desenvolvimento" },
  { id: "series", label: "Quero produzir uma pequena série" },
  { id: "customize", label: "Quero personalizar um produto existente" },
  { id: "other", label: "Outro" },
];

const REQ_KEYS = [
  ["exterior", "Uso exterior"], ["heat", "Exposição ao calor"], ["water", "Contacto com água"],
  ["food", "Contacto com alimentos"], ["load", "Carga mecânica"], ["flexible", "Flexibilidade"],
  ["impact", "Resistência ao impacto"], ["chemical", "Resistência química"],
  ["aesthetic", "Aparência visual"], ["precision", "Precisão dimensional"],
];

export default function QuoteWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    request_type: "print_file",
    title: "",
    description: "",
    quantity: 1,
    dimensions: "",
    deadline: "",
    urgency: "normal",
    material_pref: "",
    color_pref: "",
    requirements: {},
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    company: user?.company?.name || "",
    vat_number: user?.company?.vat_number || "",
    locality: "",
    consent_terms: false,
    consent_files_owner: false,
    consent_gdpr: false,
    file_ids: [],
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  async function uploadFiles(fileList) {
    setUploading(true);
    const uploaded = [];
    for (const f of Array.from(fileList)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data: r } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(r);
      } catch (e) {
        toast.error(`${f.name}: ${apiError(e)}`);
      }
    }
    setFiles((prev) => [...prev, ...uploaded]);
    set("file_ids", [...data.file_ids, ...uploaded.map((u) => u.file_id)]);
    setUploading(false);
    if (uploaded.length) toast.success(`${uploaded.length} ficheiro(s) carregado(s)`);
  }

  async function submit() {
    if (!data.consent_terms || !data.consent_files_owner || !data.consent_gdpr) {
      toast.error("É necessário aceitar os três consentimentos");
      return;
    }
    setSubmitting(true);
    try {
      const { data: r } = await api.post("/quotes", data);
      toast.success(`Pedido ${r.code} criado`);
      nav(user ? "/portal/orcamentos" : "/", { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  const canNext =
    (step === 1 && data.request_type) ||
    (step === 2 && data.name && data.email) ||
    (step === 3 && data.title && data.description) ||
    step === 4 || step === 5;

  return (
    <div className="anim-in">
      <div className="border-b border-border">
        <div className="container-x py-14">
          <p className="tech-label mb-3">Pedido de orçamento · cadditive</p>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tighter">Vamos falar do teu projeto.</h1>
          <p className="mt-4 text-muted-foreground max-w-2xl">Preenche as etapas. Respondemos em 48h com uma proposta transparente.</p>
        </div>
      </div>

      <div className="container-x py-10 max-w-4xl">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-10 flex-wrap mono text-[10px] uppercase tracking-widest">
          {["Tipo", "Identificação", "Descrição", "Requisitos", "Ficheiros", "Confirmação"].map((s, i) => (
            <div key={s} className={`flex items-center gap-2 ${i + 1 === step ? "text-brand" : i + 1 < step ? "text-foreground" : "text-muted-foreground"}`}>
              <span className={`w-6 h-6 flex items-center justify-center border ${i + 1 === step ? "border-brand" : "border-border"}`}>0{i + 1}</span>
              <span>{s}</span>
              {i < 5 && <span className="w-6 border-t border-border" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="grid sm:grid-cols-2 gap-3">
            {TYPES.map((t) => (
              <button key={t.id} onClick={() => set("request_type", t.id)} data-testid={`quote-type-${t.id}`}
                className={`text-left p-5 border transition-all ${data.request_type === t.id ? "border-brand bg-brand/5" : "border-border hover:border-foreground"}`}>
                <div className="mono text-[10px] uppercase text-muted-foreground">Opção</div>
                <div className="font-medium mt-1">{t.label}</div>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome *"><input data-testid="quote-name" className="input" value={data.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Email *"><input data-testid="quote-email" type="email" className="input" value={data.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Telefone"><input data-testid="quote-phone" className="input" value={data.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Localidade"><input data-testid="quote-locality" className="input" value={data.locality} onChange={(e) => set("locality", e.target.value)} /></Field>
            <Field label="Empresa (opcional)"><input data-testid="quote-company" className="input" value={data.company} onChange={(e) => set("company", e.target.value)} /></Field>
            <Field label="NIF (opcional)"><input data-testid="quote-vat" className="input" value={data.vat_number} onChange={(e) => set("vat_number", e.target.value)} /></Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <Field label="Título do projeto *"><input data-testid="quote-title" className="input" value={data.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex.: suporte para telemóvel na secretária" /></Field>
            <Field label="Descrição detalhada *">
              <textarea data-testid="quote-description" className="input min-h-[140px]" value={data.description} onChange={(e) => set("description", e.target.value)}
                placeholder="Utilização, problema a resolver, contexto, referências…" />
            </Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Quantidade"><input data-testid="quote-quantity" type="number" min={1} className="input" value={data.quantity} onChange={(e) => set("quantity", Number(e.target.value))} /></Field>
              <Field label="Dimensões aproximadas"><input data-testid="quote-dimensions" className="input" value={data.dimensions} onChange={(e) => set("dimensions", e.target.value)} placeholder="Ex.: 80×40×20 mm" /></Field>
              <Field label="Prazo pretendido"><input data-testid="quote-deadline" type="date" className="input" value={data.deadline} onChange={(e) => set("deadline", e.target.value)} /></Field>
            </div>
            <Field label="Nível de urgência">
              <select data-testid="quote-urgency" className="input" value={data.urgency} onChange={(e) => set("urgency", e.target.value)}>
                <option value="low">Baixa · sem pressa</option>
                <option value="normal">Normal</option>
                <option value="high">Alta · urgente</option>
              </select>
            </Field>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <div>
              <p className="tech-label mb-3">Requisitos técnicos</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {REQ_KEYS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 border border-border px-3 py-2 text-sm cursor-pointer hover:border-foreground">
                    <input type="checkbox" data-testid={`quote-req-${k}`}
                      checked={!!data.requirements[k]}
                      onChange={(e) => set("requirements", { ...data.requirements, [k]: e.target.checked })} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Material pretendido"><input data-testid="quote-material" className="input" value={data.material_pref} onChange={(e) => set("material_pref", e.target.value)} placeholder="PLA, PETG, ASA, TPU… ou 'a recomendar'" /></Field>
              <Field label="Cor pretendida"><input data-testid="quote-color" className="input" value={data.color_pref} onChange={(e) => set("color_pref", e.target.value)} placeholder="Preto, branco, laranja…" /></Field>
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <label className="crosshair-corners flex flex-col items-center justify-center border border-dashed border-border p-10 cursor-pointer hover:border-brand" data-testid="quote-file-uploader">
              <Upload size={28} className="text-brand" />
              <p className="mt-3 font-medium">Carregar ficheiros</p>
              <p className="tech-label mt-1">STL · 3MF · STEP · OBJ · IGES · DXF · DWG · PDF · JPG · PNG · ZIP</p>
              <p className="mono text-[10px] mt-1 text-muted-foreground">máx. 100 MB por ficheiro</p>
              <input type="file" multiple hidden onChange={(e) => uploadFiles(e.target.files)} accept=".stl,.3mf,.step,.stp,.obj,.iges,.igs,.dxf,.dwg,.pdf,.jpg,.jpeg,.png,.webp,.zip" />
            </label>
            {uploading && <p className="tech-label mt-4">a carregar…</p>}
            {files.length > 0 && (
              <div className="mt-6 space-y-2">
                {files.map((f) => (
                  <div key={f.file_id} className="flex items-center justify-between border border-border px-4 py-2 text-sm">
                    <div className="flex items-center gap-3"><FileText size={16} className="text-muted-foreground" /> {f.original_filename}</div>
                    <div className="flex items-center gap-3">
                      <span className="mono text-[10px] text-muted-foreground uppercase">{(f.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => { setFiles((p) => p.filter((x) => x.file_id !== f.file_id)); set("file_ids", data.file_ids.filter((id) => id !== f.file_id)); }} className="text-muted-foreground hover:text-destructive"><X size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 6 && (
          <div className="space-y-6">
            <Summary data={data} filesCount={files.length} />
            <div className="space-y-3">
              <Consent id="c1" testid="quote-consent-terms" checked={data.consent_terms} onChange={(v) => set("consent_terms", v)}
                label="Aceito os termos e condições e a política de privacidade da cadditive." />
              <Consent id="c2" testid="quote-consent-files" checked={data.consent_files_owner} onChange={(v) => set("consent_files_owner", v)}
                label="Confirmo que possuo autorização para utilizar os ficheiros enviados." />
              <Consent id="c3" testid="quote-consent-gdpr" checked={data.consent_gdpr} onChange={(v) => set("consent_gdpr", v)}
                label="Consinto o tratamento dos meus dados pessoais para processar este pedido (RGPD)." />
            </div>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="btn-outline text-sm disabled:opacity-40" data-testid="quote-prev">
            <ArrowLeft size={14} /> Anterior
          </button>
          {step < 6 ? (
            <button onClick={() => setStep((s) => s + 1)} disabled={!canNext} className="btn-primary text-sm disabled:opacity-40" data-testid="quote-next">
              Seguinte <ArrowRight size={14} />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} className="btn-primary text-sm" data-testid="quote-submit">
              <CheckCircle2 size={14} /> Submeter pedido
            </button>
          )}
        </div>
      </div>

      <style>{`.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:.6rem .75rem;font-size:.875rem;outline:none;transition:border-color .15s}.input:focus{border-color:var(--brand)}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="tech-label block mb-2">{label}</span>
    {children}
  </label>
);
const Consent = ({ id, testid, checked, onChange, label }) => (
  <label className="flex items-start gap-3 border border-border p-4 cursor-pointer hover:border-foreground text-sm">
    <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5" />
    <span>{label}</span>
  </label>
);

const Summary = ({ data, filesCount }) => (
  <div className="border border-border p-6 crosshair-corners space-y-2 text-sm bg-card">
    <p className="tech-label">Resumo do pedido</p>
    <Row k="Tipo" v={TYPES.find((t) => t.id === data.request_type)?.label} />
    <Row k="Cliente" v={`${data.name} · ${data.email}`} />
    <Row k="Título" v={data.title} />
    <Row k="Quantidade" v={data.quantity} />
    <Row k="Prazo" v={data.deadline || "—"} />
    <Row k="Material" v={data.material_pref || "a recomendar"} />
    <Row k="Ficheiros" v={filesCount} />
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-border py-1.5">
    <span className="tech-label">{k}</span>
    <span className="font-medium text-right">{v}</span>
  </div>
);
